import { useEffect, useState } from "react";
import { X, Save, Loader2, Trash2, ImagePlus, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BODY_TYPES = ["Sedan", "SUV", "Hatchback", "Pickup", "Coupe", "Convertible", "Van"];
const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const CONDITIONS = ["New", "Used", "Excellent"];
const LOCATIONS = ["Phnom Penh", "Siem Reap", "Sihanoukville", "Battambang"];
const STATUSES = ["pending", "approved", "rejected", "sold", "rented"];

const empty = {
  listing_type: "sale",
  brand: "", model: "", year: new Date().getFullYear(), variant: "",
  price: "", mileage: "", transmission: "Automatic", fuel_type: "Petrol",
  body_type: "Sedan", engine_size: "", colour: "", condition: "Used",
  location: "Phnom Penh", description: "", images: [],
  daily_price: "", weekly_price: "", monthly_price: "", deposit: "",
  insurance_included: false, driver_included: false, self_drive_available: true,
  minimum_rental_days: 1, rental_terms: "", pickup_location: "", delivery_available: false,
  status: "approved", featured: false, suspended: false, price_drop: false,
};

export default function ListingEditorModal({ listingId, onClose, onChanged }) {
  const isEdit = Boolean(listingId);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState(empty);

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data));
    if (isEdit) {
      setLoading(true);
      api.get(`/listings/${listingId}`)
        .then(({ data }) => {
          const merged = { ...empty };
          for (const k of Object.keys(empty)) {
            if (data[k] !== undefined && data[k] !== null) merged[k] = data[k];
          }
          setForm(merged);
        })
        .catch(() => { toast.error("Failed to load listing"); onClose(); })
        .finally(() => setLoading(false));
    }
  }, [listingId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = data.url || "";
        uploaded.push(url.startsWith("http") ? url : `${BACKEND_URL}${url}`);
      }
      update("images", [...(form.images || []), ...uploaded]);
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const removeImage = (i) => update("images", form.images.filter((_, idx) => idx !== i));

  const buildPayload = () => ({
    ...form,
    year: Number(form.year) || new Date().getFullYear(),
    price: form.listing_type === "sale" ? (Number(form.price) || 0) : 0,
    mileage: form.mileage === "" ? null : Number(form.mileage),
    daily_price: form.daily_price === "" ? null : Number(form.daily_price),
    weekly_price: form.weekly_price === "" ? null : Number(form.weekly_price),
    monthly_price: form.monthly_price === "" ? null : Number(form.monthly_price),
    deposit: form.deposit === "" ? null : Number(form.deposit),
    minimum_rental_days: Number(form.minimum_rental_days || 1),
  });

  const save = async () => {
    if (!form.brand || !form.model || !form.location) {
      toast.error("Brand, model and location are required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.patch(`/admin/listings/${listingId}`, payload);
        toast.success("Listing updated");
      } else {
        // Admin create: use POST /listings (creates as admin user with status=pending)
        // then immediately patch to set status & featured & images
        const { data } = await api.post("/listings", payload);
        await api.patch(`/admin/listings/${data.id}`, { status: form.status, featured: !!form.featured, suspended: !!form.suspended });
        toast.success("Listing created");
      }
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const toggleSuspend = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/listings/${listingId}/status`, { suspended: !form.suspended });
      update("suspended", !form.suspended);
      toast.success(form.suspended ? "Listing un-suspended" : "Listing suspended");
      onChanged?.();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Permanently delete this listing? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/listings/${listingId}`);
      toast.success("Listing deleted");
      onChanged?.();
      onClose();
    } catch (e) { toast.error("Delete failed"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Backdrop onClose={onClose}>
        <div className="bg-white rounded-2xl p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      </Backdrop>
    );
  }

  const isRental = form.listing_type === "rental";

  return (
    <Backdrop onClose={onClose}>
      <div className="bg-white w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl" data-testid="listing-editor-modal">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3 z-10">
          <h2 className="font-display font-bold text-lg text-cm-primary flex-1 truncate">{isEdit ? "Edit listing" : "Create listing"}</h2>
          {form.suspended && <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-full bg-red-100 text-red-700">suspended</span>}
          <button onClick={onClose} data-testid="listing-modal-close" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {/* Type */}
          <div className="grid grid-cols-2 gap-2">
            {[{ id: "sale", label: "For Sale" }, { id: "rental", label: "For Rent" }].map(o => (
              <button key={o.id} type="button" data-testid={`type-${o.id}`} onClick={() => update("listing_type", o.id)}
                className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${form.listing_type === o.id ? 'border-cm-accent bg-red-50 text-cm-primary' : 'border-slate-200 text-slate-600'}`}>
                {o.label}
              </button>
            ))}
          </div>

          {/* Vehicle */}
          <Section title="Vehicle">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Brand">
                <select data-testid="le-brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} className={inputCls}>
                  <option value="">Select…</option>
                  {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Model"><input data-testid="le-model" value={form.model} onChange={(e) => update("model", e.target.value)} className={inputCls} /></Field>
              <Field label="Year"><input data-testid="le-year" type="number" value={form.year} onChange={(e) => update("year", e.target.value)} className={inputCls} /></Field>
              <Field label="Variant"><input value={form.variant} onChange={(e) => update("variant", e.target.value)} className={inputCls} /></Field>
              <Field label="Body type">
                <select value={form.body_type} onChange={(e) => update("body_type", e.target.value)} className={inputCls}>
                  {BODY_TYPES.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Fuel">
                <select value={form.fuel_type} onChange={(e) => update("fuel_type", e.target.value)} className={inputCls}>
                  {FUELS.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Transmission">
                <select value={form.transmission} onChange={(e) => update("transmission", e.target.value)} className={inputCls}>
                  {TRANSMISSIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Condition">
                <select value={form.condition} onChange={(e) => update("condition", e.target.value)} className={inputCls}>
                  {CONDITIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Colour"><input value={form.colour} onChange={(e) => update("colour", e.target.value)} className={inputCls} /></Field>
              <Field label="Engine"><input value={form.engine_size} onChange={(e) => update("engine_size", e.target.value)} className={inputCls} placeholder="e.g. 2.0L Turbo" /></Field>
            </div>
          </Section>

          {/* Pricing */}
          <Section title={isRental ? "Rental pricing" : "Pricing"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isRental ? (
                <>
                  <Field label="Daily $"><input data-testid="le-daily" type="number" value={form.daily_price} onChange={(e) => update("daily_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Weekly $"><input type="number" value={form.weekly_price} onChange={(e) => update("weekly_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Monthly $"><input type="number" value={form.monthly_price} onChange={(e) => update("monthly_price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Deposit $"><input type="number" value={form.deposit} onChange={(e) => update("deposit", e.target.value)} className={inputCls} /></Field>
                  <Field label="Min rental days"><input type="number" value={form.minimum_rental_days} onChange={(e) => update("minimum_rental_days", e.target.value)} className={inputCls} /></Field>
                  <Field label="Pickup location"><input value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} className={inputCls} /></Field>
                </>
              ) : (
                <>
                  <Field label="Sale price $"><input data-testid="le-price" type="number" value={form.price} onChange={(e) => update("price", e.target.value)} className={inputCls} /></Field>
                  <Field label="Mileage (km)"><input type="number" value={form.mileage} onChange={(e) => update("mileage", e.target.value)} className={inputCls} /></Field>
                </>
              )}
              <Field label="Location">
                <select data-testid="le-location" value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls}>
                  {LOCATIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description">
              <textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} className={inputCls + " resize-none"} placeholder="Tell buyers about this car…" />
            </Field>
          </Section>

          {/* Photos */}
          <Section title="Photos">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(form.images || []).map((src, i) => (
                <div key={`img-${src}-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label data-testid="le-upload" className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-cm-accent flex flex-col items-center justify-center gap-1 cursor-pointer text-slate-500 hover:text-cm-accent">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                <span className="text-[10px] font-medium">{uploading ? "Up…" : "Add"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImages(Array.from(e.target.files || []))} />
              </label>
            </div>
          </Section>

          {/* Admin controls */}
          <Section title="Admin controls">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Status">
                <select data-testid="le-status" value={form.status} onChange={(e) => update("status", e.target.value)} className={inputCls}>
                  {STATUSES.map(o => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Featured">
                <button type="button" data-testid="le-featured-toggle" onClick={() => update("featured", !form.featured)} className={`w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 ${form.featured ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}>
                  {form.featured ? "★ Featured (on)" : "☆ Not featured"}
                </button>
              </Field>
              <Field label="Price drop">
                <button type="button" data-testid="le-price-drop-toggle" onClick={() => update("price_drop", !form.price_drop)} className={`w-full px-3 py-2.5 rounded-xl text-sm font-semibold border-2 ${form.price_drop ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 text-slate-600'}`}>
                  {form.price_drop ? "▼ Price Drop (on)" : "○ No price drop"}
                </button>
              </Field>
            </div>
          </Section>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-4 flex flex-wrap gap-2 justify-between items-center">
          {isEdit ? (
            <div className="flex gap-2">
              <button onClick={toggleSuspend} disabled={saving} data-testid="le-suspend-btn" className={`px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 ${form.suspended ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'} disabled:opacity-60`}>
                {form.suspended ? <><Eye className="w-4 h-4" /> Un-suspend</> : <><EyeOff className="w-4 h-4" /> Suspend</>}
              </button>
              <button onClick={remove} disabled={saving} data-testid="le-delete-btn" className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="le-save-btn" className="px-5 py-2.5 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-semibold flex items-center gap-2 press-scale disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isEdit ? "Save changes" : "Create listing"}
            </button>
          </div>
        </div>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {children}
    </div>
  );
}
function Section({ title, children }) {
  return <div><h3 className="font-display font-bold text-cm-primary text-sm uppercase tracking-wider mb-2">{title}</h3>{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:border-cm-accent outline-none";
